import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Award, Ban, XCircle } from "lucide-react";
import { PageLoader } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  type PublicProceeding,
  useMyProceedingDetail,
} from "@/features/rfq/api/proceedings";
import { ProceedingOffererDetail } from "@/features/rfq/components/ProceedingOffererDetail";
import { UnreadCommunicationsBanner } from "@/features/rfq/components/UnreadCommunicationsBanner";

/**
 * Podstrona jednego postępowania dla podwykonawcy — pełny widok, do którego wchodzi
 * z listy. Otwarcie odnotowuje backend (statystyki „kto kiedy zobaczył" po stronie GK).
 */
export function MyProceedingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useMyProceedingDetail(id);
  usePageTitle(data ? data.title : "Postępowanie ofertowe");

  if (isLoading) return <PageLoader />;
  if (isError || !data)
    return (
      <div className="max-w-3xl">
        <Link
          to="/contractor/proceedings"
          className="mb-5 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
        >
          <ArrowLeft size={14} /> Postępowania ofertowe
        </Link>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Nie znaleziono postępowania lub nie masz do niego dostępu.
        </p>
      </div>
    );

  return (
    <div>
      <Link
        to="/contractor/proceedings"
        className="mb-5 inline-flex items-center gap-1 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft size={14} /> Postępowania ofertowe
      </Link>
      <UnreadCommunicationsBanner proceedingId={data.id} />
      <OutcomeBanner outcome={data.outcome} />
      <ProceedingOffererDetail proceeding={data} />
    </div>
  );
}

/** Wynik postępowania oczami oferenta — pokazywany dopiero po rozstrzygnięciu. */
function OutcomeBanner({ outcome }: { outcome: PublicProceeding["outcome"] }) {
  if (!outcome) return null;
  const map = {
    won: {
      icon: Award,
      text: "Twoja oferta została wybrana jako najkorzystniejsza. Skontaktujemy się w sprawie dalszych kroków.",
      cls: "border-green-600/40 bg-green-50 text-green-800 dark:bg-green-950/40 dark:text-green-300",
    },
    lost: {
      icon: XCircle,
      text: "Postępowanie rozstrzygnięte — Twoja oferta nie została wybrana. Dziękujemy za udział.",
      cls: "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]",
    },
    awarded: {
      icon: Award,
      text: "Postępowanie zostało rozstrzygnięte.",
      cls: "border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]",
    },
    cancelled: {
      icon: Ban,
      text: "Postępowanie zostało zakończone bez wyboru którejkolwiek z ofert.",
      cls: "border-amber-600/40 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
    },
  }[outcome];
  const Icon = map.icon;
  return (
    <div className={`mb-4 flex items-start gap-2 border px-4 py-3 text-sm ${map.cls}`}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <p>{map.text}</p>
    </div>
  );
}
